import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, emptyBinding } from '../lib/api';
import { Badge, FailureBadge, Loading, Meter, Toast, useAsync } from '../components/common';
import { FILE_TYPE_KEYS } from '../types';
import type {
  DspUsage,
  LambdaBinding,
  LambdaBindingRow,
  LambdaDetail,
  OpsArea,
  Principal,
} from '../types';

const AREA_PATH: Record<OpsArea, string> = {
  settlement_operations: '/settlement',
  lead_scoring: '/lead-scoring',
  general: '/general',
};

export default function LambdaDetailPage({ principal }: { principal: Principal }) {
  const { lambdaKey = '' } = useParams();
  const { data, error, loading, reload } = useAsync(() => api.getLambda(lambdaKey), [lambdaKey]);
  const [toast, setToast] = useState<{ message: string; tone: 'info' | 'error' } | null>(null);
  const [usageFilter, setUsageFilter] = useState<'all' | 'active' | 'disabled'>('all');

  if (loading || !data) return <Loading error={error} />;

  const r = data.registry;
  const readOnly = principal.role !== 'admin';
  const area = data.ops_area || r.ops_area || 'general';
  const areaMeta = data.ops_area_meta;
  const usageRows = (data.dsp_usage || []).filter((row) => {
    if (usageFilter === 'all') return true;
    if (usageFilter === 'active') return row.status === 'active';
    return row.status !== 'active';
  });

  return (
    <>
      <div className="page-head">
        <div>
          <div className="small muted" style={{ marginBottom: 4 }}>
            <Link to={AREA_PATH[area]}>{areaMeta?.title || area}</Link>
          </div>
          <h1>{r.display_name}</h1>
          <p>
            <code>{r.lambda_key}</code> ·{' '}
            <code>
              {r.repo === 'settlement'
                ? 'skybrigde-settlement-management'
                : 'skybridge_lamdba_functions'}
              /{r.code_path}
            </code>
          </p>
          <div className="chips" style={{ marginTop: 8 }}>
            <Badge tone="ok">{areaMeta?.title || area}</Badge>
            <Badge tone="info">{r.trigger_type}</Badge>
            <FailureBadge mode={r.failure_mode} />
            {r.required_for_active_parent && (
              <Badge tone="danger">required for active parents</Badge>
            )}
            {!r.partner_aware && <Badge>partner-agnostic</Badge>}
            <Badge tone={r.status === 'active' ? 'ok' : 'warn'}>{r.status}</Badge>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="small muted">Binding coverage</div>
          <Meter value={data.config_completeness} />
        </div>
      </div>

      {areaMeta?.blurb && <div className="callout info">{areaMeta.blurb}</div>}

      {r.danger_flags.length > 0 && (
        <div className="card">
          <h2>
            Danger flags
            <span className="hint">clears only when the code fix lands</span>
          </h2>
          {r.danger_flags.map((flag, index) => (
            <div key={index} className="callout danger">
              {flag}
            </div>
          ))}
        </div>
      )}

      {/* 1 · Summary */}
      <div className="card">
        <h2>1 · What this function does</h2>
        <p style={{ marginTop: 0 }}>{r.operation_summary || <span className="muted">—</span>}</p>
        {r.notes && <div className="callout info">{r.notes}</div>}
        {r.steps.length > 0 && (
          <>
            <div className="small muted" style={{ margin: '12px 0 6px' }}>
              Sequence
            </div>
            <ol className="steps">
              {r.steps.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ol>
          </>
        )}
      </div>

      {/* 2 · Per-DSP usage — the main ask */}
      <div className="card">
        <h2>
          2 · What it does for each DSP
          <span className="hint">resolved from parent + binding + published config shape</span>
        </h2>
        <p className="small muted" style={{ marginTop: 0 }}>
          This is the operator view of runtime behaviour: for every DSP parent, whether the
          function is on, which paths and ids it uses, and the prose of what happens for that
          tenant. Edit bindings in section 6; publish for Lambdas to pick up changes.
        </p>
        <div className="row" style={{ marginBottom: 12 }}>
          <label className="field" style={{ flex: '0 1 220px' }}>
            <span>Show</span>
            <select
              value={usageFilter}
              onChange={(e) => setUsageFilter(e.target.value as typeof usageFilter)}
            >
              <option value="all">All parents</option>
              <option value="active">Enabled for this function</option>
              <option value="disabled">Disabled / N/A</option>
            </select>
          </label>
        </div>
        {usageRows.length === 0 ? (
          <div className="callout">No parents match this filter.</div>
        ) : (
          usageRows.map((row) => <DspUsageCard key={row.parent_key} row={row} />)
        )}
      </div>

      {/* 3 · Trigger */}
      <div className="card">
        <h2>
          3 · Trigger
          <span className="hint">{data.trigger_summary}</span>
        </h2>
        <pre className="json">{JSON.stringify(r.trigger_detail, null, 2)}</pre>
      </div>

      {/* 4 · I/O */}
      <div className="card">
        <h2>4 · Inputs and outputs</h2>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Role</th>
                <th>Kind</th>
                <th>Key</th>
                <th>Direction</th>
              </tr>
            </thead>
            <tbody>
              {[
                ...r.inputs.map((io) => ({ role: 'input', ...io })),
                ...r.outputs.map((io) => ({ role: 'output', ...io })),
              ].map((io, index) => (
                <tr key={index}>
                  <td>{io.role}</td>
                  <td>
                    <Badge tone="info">{io.kind}</Badge>
                  </td>
                  <td className="mono">{io.key}</td>
                  <td>{io.direction}</td>
                </tr>
              ))}
              {r.inputs.length + r.outputs.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted">
                    Nothing recorded.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 5 · Routing rules */}
      <div className="card">
        <h2>5 · Routing and failure posture</h2>
        <p style={{ marginTop: 0 }}>
          <FailureBadge mode={r.failure_mode} />
        </p>
        {r.fail_closed_rules.length === 0 ? (
          <div className="callout warn">
            No fail-closed guarantee is recorded for this function.
          </div>
        ) : (
          r.fail_closed_rules.map((rule, index) => (
            <div key={index} className="callout ok">
              {rule}
            </div>
          ))
        )}
      </div>

      {/* 6 · Bindings */}
      <BindingMatrix
        detail={data}
        readOnly={readOnly}
        onSaved={() => {
          reload();
          setToast({ message: 'Bindings saved.', tone: 'info' });
        }}
        onError={(m) => setToast({ message: m, tone: 'error' })}
      />

      {/* 7 · Legacy env */}
      <div className="card">
        <h2>
          7 · Legacy environment and terraform surface
          <span className="hint">read-only migration aid</span>
        </h2>
        <div className="row">
          <div style={{ flex: '1 1 320px' }}>
            <div className="small muted" style={{ marginBottom: 6 }}>
              Environment variables
            </div>
            <div className="chips">
              {r.legacy_env_vars.length === 0 ? (
                <span className="muted small">none</span>
              ) : (
                r.legacy_env_vars.map((v) => <Badge key={v}>{v}</Badge>)
              )}
            </div>
          </div>
          <div style={{ flex: '1 1 320px' }}>
            <div className="small muted" style={{ marginBottom: 6 }}>
              Terraform variables
            </div>
            <div className="chips">
              {r.terraform_vars.length === 0 ? (
                <span className="muted small">none</span>
              ) : (
                r.terraform_vars.map((v) => <Badge key={v}>{v}</Badge>)
              )}
            </div>
          </div>
        </div>
        {r.iam_summary && (
          <>
            <div className="small muted" style={{ margin: '14px 0 6px' }}>
              Execution role reach
            </div>
            <div className="callout">{r.iam_summary}</div>
          </>
        )}
      </div>

      {toast && <Toast {...toast} onDone={() => setToast(null)} />}
    </>
  );
}

function DspUsageCard({ row }: { row: DspUsage }) {
  const tone =
    row.status === 'active' ? 'ok' : row.status === 'disabled' ? 'warn' : 'info';
  const cfg = row.config_used;
  return (
    <div className="dsp-usage">
      <div className="dsp-usage-head">
        <div>
          <Link to={`/parents/${row.parent_key}`}>
            <strong>{row.display_name}</strong>
          </Link>
          <div className="small muted mono">
            {row.parent_key} · Zoho parent company “{row.parent_company}”
          </div>
        </div>
        <div className="chips">
          <Badge tone={tone}>{row.status}</Badge>
          <Badge tone={row.parent_status === 'active' ? 'ok' : 'warn'}>{row.parent_status}</Badge>
        </div>
      </div>
      <p style={{ margin: '10px 0' }}>{row.narrative}</p>
      {row.status === 'active' && (
        <dl className="kv" style={{ fontSize: 12 }}>
          <dt>bucket</dt>
          <dd className="mono">{cfg.bucket ?? '—'}</dd>
          <dt>file types</dt>
          <dd className="mono">
            {cfg.file_type_keys.length ? cfg.file_type_keys.join(', ') : '—'}
          </dd>
          <dt>eventbridge</dt>
          <dd className="mono">{cfg.eventbridge_prefix || '—'}</dd>
          <dt>snowflake</dt>
          <dd className="mono">{cfg.snowflake_profile_key ?? '—'}</dd>
          <dt>zoho partner ids</dt>
          <dd className="mono">
            {cfg.zoho_partner_ids?.length ? cfg.zoho_partner_ids.join(', ') : '—'}
          </dd>
          <dt>webhook</dt>
          <dd>
            {cfg.webhook_enabled
              ? `on (${cfg.integration_key || 'no integration_key'})`
              : 'off'}
          </dd>
          {Object.keys(cfg.extra || {}).length > 0 && (
            <>
              <dt>extra</dt>
              <dd className="mono">{JSON.stringify(cfg.extra)}</dd>
            </>
          )}
          <dt>resolved keys</dt>
          <dd className="mono">
            {cfg.resolved_keys?.length
              ? cfg.resolved_keys.map((p) => (
                  <div key={p.file_type_key}>
                    {p.file_type_key}: {p.s3_uri ?? `⚠ ${p.error}`}
                  </div>
                ))
              : '—'}
          </dd>
        </dl>
      )}
      <details style={{ marginTop: 8 }}>
        <summary className="small muted">How the runtime client reads this</summary>
        <pre className="json" style={{ marginTop: 8 }}>
          {row.how_runtime_reads.join('\n')}
        </pre>
      </details>
    </div>
  );
}

function BindingMatrix({
  detail,
  readOnly,
  onSaved,
  onError,
}: {
  detail: LambdaDetail;
  readOnly: boolean;
  onSaved: () => void;
  onError: (message: string) => void;
}) {
  const lambdaKey = detail.registry.lambda_key;
  const [draft, setDraft] = useState<Record<string, LambdaBinding> | null>(null);

  const bindings =
    draft ??
    Object.fromEntries(
      detail.bindings
        .filter((row) => row.binding)
        .map((row) => [row.parent_key, row.binding as LambdaBinding]),
    );

  function update(row: LambdaBindingRow, patch: Partial<LambdaBinding>) {
    const current = bindings[row.parent_key] ?? emptyBinding(row.parent_key, lambdaKey);
    setDraft({ ...bindings, [row.parent_key]: { ...current, ...patch } });
  }

  async function save() {
    try {
      await api.putLambdaBindings(
        lambdaKey,
        Object.values(bindings).filter(
          (b) => b.enabled || b.file_type_keys.length > 0 || b.notes,
        ),
      );
      setDraft(null);
      onSaved();
    } catch (err) {
      onError((err as Error).message);
    }
  }

  return (
    <div className="card">
      <h2>
        6 · Edit per-parent bindings
        <span className="hint">source of truth written into the published snapshot</span>
      </h2>

      {detail.unbound_active_parents.length > 0 && (
        <div className="callout warn">
          Active parents with no enabled binding: {detail.unbound_active_parents.join(', ')}
        </div>
      )}

      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Parent</th>
              <th>Enabled</th>
              <th>File types</th>
              <th>Overrides</th>
              <th>Webhook / extra / notes</th>
              <th>Effective config</th>
              <th>Resolved keys</th>
            </tr>
          </thead>
          <tbody>
            {detail.bindings.map((row) => {
              const binding = bindings[row.parent_key];
              return (
                <tr key={row.parent_key}>
                  <td>
                    <Link to={`/parents/${row.parent_key}`}>{row.display_name}</Link>
                    <div className="small muted mono">{row.parent_key}</div>
                    <Badge tone={row.parent_status === 'active' ? 'ok' : 'warn'}>
                      {row.parent_status}
                    </Badge>
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={binding?.enabled ?? false}
                      onChange={(e) => update(row, { enabled: e.target.checked })}
                      disabled={readOnly}
                    />
                  </td>
                  <td style={{ minWidth: 190 }}>
                    <div className="chips">
                      {FILE_TYPE_KEYS.map((key) => {
                        const on = binding?.file_type_keys.includes(key) ?? false;
                        return (
                          <button
                            key={key}
                            className={on ? 'primary' : ''}
                            style={{ padding: '1px 7px', fontSize: 11 }}
                            disabled={readOnly}
                            onClick={() =>
                              update(row, {
                                file_type_keys: on
                                  ? (binding?.file_type_keys ?? []).filter((k) => k !== key)
                                  : [...(binding?.file_type_keys ?? []), key],
                              })
                            }
                          >
                            {key.replace(/_/g, '-')}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                  <td style={{ minWidth: 180 }}>
                    <label className="field" style={{ marginBottom: 6 }}>
                      <span>Bucket</span>
                      <input
                        className="mono small"
                        placeholder="parent default"
                        value={binding?.bucket_override ?? ''}
                        onChange={(e) => update(row, { bucket_override: e.target.value || null })}
                        disabled={readOnly}
                      />
                    </label>
                    <label className="field" style={{ marginBottom: 0 }}>
                      <span>Snowflake</span>
                      <input
                        className="mono small"
                        placeholder="parent default"
                        value={binding?.snowflake_profile_key ?? ''}
                        onChange={(e) =>
                          update(row, { snowflake_profile_key: e.target.value || null })
                        }
                        disabled={readOnly}
                      />
                    </label>
                  </td>
                  <td style={{ minWidth: 220 }}>
                    <label className="inline" style={{ marginBottom: 8 }}>
                      <input
                        type="checkbox"
                        checked={binding?.webhook_enabled ?? false}
                        onChange={(e) => update(row, { webhook_enabled: e.target.checked })}
                        disabled={readOnly}
                      />
                      webhook
                    </label>
                    <label className="field" style={{ marginBottom: 6 }}>
                      <span>integration_key</span>
                      <input
                        className="mono small"
                        value={binding?.integration_key ?? ''}
                        onChange={(e) =>
                          update(row, { integration_key: e.target.value || null })
                        }
                        disabled={readOnly}
                      />
                    </label>
                    <label className="field" style={{ marginBottom: 6 }}>
                      <span>extra (JSON)</span>
                      <textarea
                        className="mono small"
                        rows={3}
                        defaultValue={JSON.stringify(binding?.extra ?? {}, null, 2)}
                        key={`${row.parent_key}:${JSON.stringify(binding?.extra ?? {})}`}
                        onBlur={(e) => {
                          try {
                            const parsed = JSON.parse(e.target.value || '{}');
                            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                              update(row, { extra: parsed as Record<string, unknown> });
                            } else {
                              onError('extra must be a JSON object');
                            }
                          } catch {
                            onError('extra must be valid JSON');
                          }
                        }}
                        disabled={readOnly}
                      />
                    </label>
                    <div className="small muted">e.g. source_file_type / output_file_type</div>
                    <label className="field" style={{ marginBottom: 0 }}>
                      <span>notes</span>
                      <input
                        value={binding?.notes ?? ''}
                        onChange={(e) => update(row, { notes: e.target.value })}
                        disabled={readOnly}
                      />
                    </label>
                  </td>
                  <td className="small" style={{ minWidth: 220 }}>
                    {row.effective.enabled ? (
                      <dl className="kv" style={{ fontSize: 12 }}>
                        <dt>bucket</dt>
                        <dd className="mono">
                          {row.effective.bucket}{' '}
                          <span className="muted">({row.effective.bucket_source})</span>
                        </dd>
                        <dt>snowflake</dt>
                        <dd className="mono">
                          {row.effective.snowflake_profile_key ?? '—'}{' '}
                          <span className="muted">({row.effective.snowflake_source})</span>
                        </dd>
                        <dt>zoho ids</dt>
                        <dd className="mono">
                          {row.effective.zoho_partner_ids?.length
                            ? row.effective.zoho_partner_ids.join(', ')
                            : '—'}
                        </dd>
                        {row.effective.eventbridge?.enabled && (
                          <>
                            <dt>eventbridge</dt>
                            <dd className="mono">
                              {row.effective.eventbridge.prefix}
                              {!row.effective.eventbridge.bucket_eventbridge_enabled && (
                                <div>
                                  <Badge tone="danger">bucket not EventBridge-enabled</Badge>
                                </div>
                              )}
                            </dd>
                          </>
                        )}
                      </dl>
                    ) : (
                      <span className="muted">not enabled</span>
                    )}
                  </td>
                  <td className="mono small" style={{ minWidth: 230 }}>
                    {row.previews.length === 0 ? (
                      <span className="muted">—</span>
                    ) : (
                      row.previews.map((p) => (
                        <div key={p.file_type_key}>{p.s3_uri ?? `⚠ ${p.error}`}</div>
                      ))
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!readOnly && (
        <div className="actions" style={{ marginTop: 12 }}>
          <button className="primary" onClick={save} disabled={draft === null}>
            Save bindings
          </button>
          {draft !== null && <button onClick={() => setDraft(null)}>Discard changes</button>}
        </div>
      )}
    </div>
  );
}
